<template>
    <div class="chatbot-container" :class="{ 'open': isChatOpen }">
        <div class="chat-container" v-if="isChatOpen">
            <div class="chatbot-main-header">
                <div class="chatbot-main-header-left">
                    <div class="chatbot-header-icon">
                        <img src="../../assets/images/logo/amaken-fav.png" alt="bot-logo">
                    </div>
                    <span class="online-circle"></span>
                    <h2>{{$t('support_chatBot')}} <span class="online-text">{{$t('online')}}</span></h2>
                </div>
                <button-prime class="minimize-chat" icon="pi pi-times" aria-label="minimize" @click="toggleChat" />
            </div>

            <ScrollPanel-prime style="width: 100%; height: 345px">
                <div class="chat-body">
                    <div v-for="message in messages" :key="message.id" :class="['message', message.sender === 'bot' ? 'bot-message' : 'user-message']">
                        <div v-if="message.sender === 'bot' && !message.data?.restaurants" class="bot-message-header">
                            <img src="../../assets/images/logo/amaken-fav.png" alt="bot-logo">
                            <h4>{{$t('amaken')}} -</h4>
                            <span>{{ $t('ai_agent') }}</span>
                        </div>
                        
                        <p v-if="message.isStartingLine" class="starting-line">{{ message.text }}</p>

                        <!-- Swiper Slider for Restaurant Names & Descriptions -->
                        <Swiper 
                            v-if="message.sender === 'bot' && Array.isArray(message.data?.restaurants) && message.data.restaurants.length > 0"
                            :slidesPerView="1"
                            :spaceBetween="10"
                            :pagination="{ clickable: true }"
                            :navigation="false"
                            class="chat-swiper"
                        >
                            <SwiperSlide v-for="(restaurant, index) in message.data.restaurants" :key="index">
                                <div class="cards-inner">
                                    <div class="card-image">
                                        <img v-if="restaurant.place_images && restaurant.place_images.length > 0" :src="restaurant.place_images" class="restaurant-image" alt="Restaurant Image" />
                                        <div v-else class="no-slider-image">
                                            <i class="pi pi-images"></i>
                                            <p> {{$t('no_image')}}</p>
                                        </div>
                                    </div>
                                    <h2 class="title">{{ restaurant.name }}</h2>
                                    <p class="description">{{ restaurant.place_description || 'No details available.' }}</p>
                                    <p class="cousines" v-if="restaurant.cuisines.length > 0">
                                        <svg viewBox="0 0 64 64" id="food" style="width: 2.2vh; fill: #898989;"><path d="M52.229 16.398a2.381 2.381 0 1 0-.222-4.758l-.892.041-.041-.892a2.381 2.381 0 1 0-4.758.222l.263 5.65 5.65-.263zm1.101 7.014-.032.688-.688-.032a1.836 1.836 0 1 0-.17 3.668l4.355.202.202-4.355a1.835 1.835 0 1 0-3.667-.171z"></path><circle cx="44.5" cy="4.188" r="1.25"></circle><circle cx="57" cy="7.911" r="1.25"></circle><circle cx="45.75" cy="21.661" r="1.25"></circle><path d="m11.771 16.398 5.65.263.263-5.65a2.382 2.382 0 0 0-4.758-.222l-.041.892-.892-.041a2.38 2.38 0 1 0-.222 4.758zm-4.769 7.184.202 4.355 4.355-.202a1.836 1.836 0 1 0-.17-3.668l-.688.032-.032-.688a1.836 1.836 0 1 0-3.667.171z"></path><circle cx="19.5" cy="4.188" r="1.25"></circle><circle cx="7" cy="7.911" r="1.25"></circle><circle cx="18.25" cy="21.661" r="1.25"></circle><path d="M32 27.313c2.412 0 4.375-1.963 4.375-4.375S34.412 18.563 32 18.563s-4.375 1.963-4.375 4.375 1.963 4.375 4.375 4.375zm0-7.5c1.723 0 3.125 1.402 3.125 3.125S33.723 26.063 32 26.063s-3.125-1.402-3.125-3.125 1.402-3.125 3.125-3.125zM3.899 53.563c.32 4.189 3.831 7.5 8.101 7.5h40c4.27 0 7.781-3.311 8.101-7.5H3.899z"></path><path d="M6.383 52.313h5.003c.218-7.089 4.122-13.649 10.301-17.232a.625.625 0 0 1 .627 1.081c-5.795 3.359-9.461 9.505-9.678 16.15h44.982c-.333-13.842-11.696-25-25.617-25S6.716 38.47 6.383 52.313zm25.617-20c2.673 0 5.275.506 7.735 1.505a.625.625 0 1 1-.47 1.158A19.217 19.217 0 0 0 32 33.563a.625.625 0 0 1 0-1.25z"></path></svg>
                                        {{restaurant.cuisines}}
                                    </p>
                                    <p class="location">
                                        <i class="pi pi-map-marker" />
                                        {{restaurant.place_location}}
                                    </p>

                                    <div class="card-action-btn">
                                        <router-link class="view" :to="'/place-detail/' + restaurant.place_url">
                                            View
                                        </router-link>
                                        <a class="call" :href="'tel:' + restaurant.place_phone_number">
                                            Call
                                        </a>
                                    </div>
                                </div>
                            </SwiperSlide>
                        </Swiper>
                        
                        <p v-else>{{ message.text }}</p>
                    </div>
                    <div v-if="loading" class="message bot-message">
                        <div class="bot-message-header bot-message-header mb-0 pr-1">
                            <img src="../../assets/images/logo/amaken-fav.png" alt="bot-logo">
                            <h4>{{$t('amaken')}} -</h4>
                            <span>{{ $t('ai_agent') }}</span>
                            <ProgressSpinner-prime style="width: 2.6vh; height: 2.6vh" strokeWidth="3" fill="var(--surface-ground)"
                            animationDuration=".5s" aria-label="Custom ProgressSpinner" />
                        </div>
                    </div>
                </div>
            </ScrollPanel-prime>

            <div class="chat-footer">
                <input v-model="userMessage" type="text" :placeholder="$t('reply_to_amaken')" @keypress.enter="sendMessage" />
                <button-prime icon="pi pi-arrow-up" aria-label="Send" @click="sendMessage"/>
            </div>
        </div>

        <div class="chatbot-icon" @click="toggleChat" @mouseover="handleMouseOver" @mouseleave="handleMouseLeave">
            <div class="ai-support-text hide-mobile" v-if="isHovered"> {{ $t('ask_our_ai') }} </div>
            <div class="chatbot-icon-area">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" width="24" height="24" stroke-width="2"> <path d="M11.217 19.384a3.501 3.501 0 0 0 6.783 -1.217v-5.167l-6 -3.35"></path> <path d="M5.214 15.014a3.501 3.501 0 0 0 4.446 5.266l4.34 -2.534v-6.946"></path> <path d="M6 7.63c-1.391 -.236 -2.787 .395 -3.534 1.689a3.474 3.474 0 0 0 1.271 4.745l4.263 2.514l6 -3.348"></path> <path d="M12.783 4.616a3.501 3.501 0 0 0 -6.783 1.217v5.067l6 3.45"></path> <path d="M18.786 8.986a3.501 3.501 0 0 0 -4.446 -5.266l-4.34 2.534v6.946"></path> <path d="M18 16.302c1.391 .236 2.787 -.395 3.534 -1.689a3.474 3.474 0 0 0 -1.271 -4.745l-4.308 -2.514l-5.955 3.42"></path> </svg> 
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
// import $ from 'jquery'
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export default {
    components: {
        Swiper,
        SwiperSlide
    },
    data() {
        return {
            userMessage: '',
            messages: [{ id: 1, sender: 'bot', text: this.$t('how_can_i_help') }],
            loading: false,
            isChatOpen: false,
            isHovered: false,
        };
    },
    methods: {
        handleMouseOver() {
            this.isHovered = true;
        },
        handleMouseLeave() {
            this.isHovered = false;
        },
        toggleChat() {
            this.isChatOpen = !this.isChatOpen;
        },
        async sendMessage() {
            if (!this.userMessage.trim()) return;

            this.messages.push({ id: Date.now(), sender: 'user', text: this.userMessage });
            const messageText = this.userMessage;
            this.userMessage = '';
            this.loading = true;

            try {
                const response = await axios.post(
                    `https://amaken-chatbot.cedardigital.io/api/chat?message=${encodeURIComponent(messageText)}`,
                    null,
                    { headers: { 'Accept': 'application/json' } }
                );

                console.log("API Response:", response.data);

                // Extract the `data` field
                let responseData = response.data?.data;

                // If `data` is a string, show it as a bot response
                if (typeof responseData === 'string') {
                    this.messages.push({
                        id: Date.now(),
                        sender: 'bot',
                        text: responseData
                    });
                } else if (responseData?.starting_lines) {
                    // Show `starting_lines` message if it exists
                    this.messages.push({
                        id: Date.now(),
                        sender: 'bot',
                        text: responseData.starting_lines
                    });
                }

                // Check for restaurant data
                let records = responseData?.data || [];

                if (Array.isArray(records) && records.length > 0) {
                    const formattedRecords = records.map((restaurant) => ({
                        name: restaurant.name,
                        place_description: restaurant.place_description || "No description available.",
                        place_location: restaurant.place_location || "Location not provided",
                        place_phone_number: restaurant.place_phone_number || "No phone number",
                        place_images: restaurant.place_images?.[0] || "", // Show only the first image
                        place_url: restaurant.place_url || "#",
                        cuisines: Array.isArray(restaurant.cuisines) 
                            ? restaurant.cuisines.join(', ') 
                            : (restaurant.cuisines || "Not specified")
                    }));

                    this.messages.push({
                        id: Date.now(),
                        sender: 'bot',
                        text: 'Here are some recommended places:',
                        data: { restaurants: formattedRecords }
                    });
                }

            } catch (error) {
                console.error('Error fetching chat response:', error.response ? error.response.data : error.message);
                this.messages.push({ id: Date.now() + 2, sender: 'bot', text: 'Sorry, something went wrong. Please try again.' });
            } finally {
                this.loading = false;
            }
        }

    }
};
</script>

<style>
.chatbot-icon-area {
    width: 45px;
    height: 45px;
    background: #d1362d;
    border-radius: 1000vh;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
    transition: transform 0.3s ease-in-out;
}

/* .chatbot-icon:hover {
    transform: scale(1.1);
} */

.chatbot-icon svg {
    color: #fff !important;
    /* fill: #fff; */
    width: 30px;
    height: 30px;
}

.chatbot-icon {
    display: flex;
    align-items: center;
    column-gap: 8px;
}

.chatbot-container {
    display: flex;
    width: auto;
    position: fixed;
    right: 10px;
    bottom: 10px;
    flex-direction: column;
    align-items: flex-end;
    row-gap: 1.5vh;
    z-index: 9999;
    transition: transform 0.3s ease-in-out;
    /* transform: scale(0); */
}

.chat-container {
    width: 400px;
    height: 480px;
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    /* border: 1.2vh solid #ededed; */
    overflow: hidden;
    display: flex;
    flex-direction: column;
    background: rgb(241,251,255);
    background: -moz-linear-gradient(0deg, rgba(241,251,255,1) 25%, rgba(255,255,255,1) 100%);
    background: -webkit-linear-gradient(0deg, rgba(241,251,255,1) 25%, rgba(255,255,255,1) 100%);
    background: linear-gradient(0deg, rgba(241,251,255,1) 25%, rgba(255,255,255,1) 100%);
    filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#f1fbff",endColorstr="#ffffff",GradientType=1);
}

.chat-header {
    background: #f9f9f9;
    padding: 10px;
    font-weight: bold;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 10px;
    border-bottom: 1px solid #ddd;
}
.chat-body {
    padding: 15px;
    padding-bottom: 15px;
    flex-grow: 1;
    /* overflow-y: auto; */
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.message {
    max-width: 80%;
    padding: 10px;
    border-radius: 15px;
    font-size: 13px;
}

.bot-message {
    background: #f4f4f4;
    align-self: flex-start;
    border-bottom-left-radius: 0;
}

.user-message {
    background: #ff6b00;
    color: white;
    align-self: flex-end;
    border-bottom-right-radius: 0;
}

.chat-footer {
    display: flex;
    padding: 12px;
    background: transparent;
    position: absolute;
    bottom: 0;
    z-index: 1;
    width: 100%;
}

.chat-footer input {
    padding: 10px 15px;
    padding-right: 40px;
    border: none;
    border-radius: 1000vh;
    outline: none;
    background: #ffffff;
    height: 45px;
    width: 100%;
    font-size: 14px;
    border: 0.5vh solid #f7f7f7;
    text-align: left;
    font-family: 'Cairo', sans-serif !important;
}

.chat-footer input::placeholder {
    color: #999;
    text-align: left !important;
    font-family: 'Cairo', sans-serif !important;
    font-weight: 400;
    font-size: 1.8vh;
}

.chat-footer button {
    background: none;
    border: none;
    font-size: 18px;
    margin-left: 10px;
    cursor: pointer;
    color: #ff6b00;
}

.chat-footer button.p-button.p-component.p-button-icon-only {
    background: #ededed;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    max-width: 30px;
    min-width: 30px;
    position: absolute;
    right: 2.6vh;
    top: 50%;
    transform: translateY(-50%);
    box-shadow: none;
}

.chat-footer button.p-button.p-component.p-button-icon-only:hover { 
    background: #272727;
}

.chat-footer button.p-button.p-component.p-button-icon-only:hover span.p-button-icon { 
    color: #ffffff !important;
}

.chat-footer button.p-button.p-component.p-button-icon-only span.p-button-icon.pi.pi-arrow-up {
    color: #646363;
    font-size: 13px;
}

.bot-message-header {
    display: flex;
    align-items: center;
    column-gap: 1vh;
    margin-bottom: 0.6vh;
    padding-right: 2vh;
}

.bot-message-header img {
    width: 3vh;
}

.bot-message-header h4 {
    font-weight: 600;
}

.bot-message-header span {
    font-weight: 600;
}

button.p-button.minimize-chat {
    position: absolute;
    right: 1vh;
    top: 1vh;
    width: 3.5vh;
    height: 3.5vh;
    max-width: 3.5vh;
    border-radius: 50%;
    min-width: 3.5vh;
    padding: 0;
    background: transparent !important;
    border: 0 !important;
    cursor: pointer;
    box-shadow: none !important;
    z-index: 3;
}

button.p-button.minimize-chat span.p-button-icon.pi {
    /* color: #fff; */
    font-size: 12px;
    color: #353535;
}

.bot-message-header .p-progress-spinner-circle {
    animation: none !important;
    stroke: #9b9b9b !important;
}

.message.bot-message:has(.swiper) {
    /* max-width: 100% !important;
    width: 100%;
    min-width: 100%; */
    background: transparent !important;
    padding: 0;
}

.message.bot-message .swiper .swiper-slide {
    background: #f4f4f4;
    padding: 10px;
    border-radius: 15px;
}

.message.bot-message .swiper .swiper-slide h2.title {
    font-size: 2vh;
    font-weight: 600;
    text-align: left;
}

.message.bot-message .swiper .swiper-slide p.description {
    text-align: left;
    /* overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical; */
    margin-bottom: 0.4vh;
}

.message.bot-message .swiper {
    overflow: visible !important;
}

p.cousines {
    text-align: left;
    font-size: 12px;
    color: #898989;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
}

p.location {
    text-align: left;
    font-size: 12px;
    color: #898989;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
}

.card-action-btn {
    display: flex;
    align-items: center;
    column-gap: 5px;
    margin-top: 10px;
    color: #bababa;
}

.card-action-btn a {
    font-weight: 600;
    color: #008ff8;
    cursor: pointer;
}

.card-action-btn a.call {
    font-weight: 600;
    color: #5cba3d;
    cursor: pointer;
    background: #fff !important;
    width: 75px;
    border-radius: 5px;
    padding: 2px;
    border: 1px solid #5cba3d;
}

.card-action-btn a.view {
    font-weight: 600;
    color: #014679;
    cursor: pointer;
    background: #ffffff !important;
    width: 75px;
    border-radius: 5px;
    padding: 2px;
    border: 1px solid #014679;
}

.cards-inner {
    display: flex;
    flex-direction: column;
    row-gap: 2px;
}

.card-image {
    width: 100%;
    height: 18vh;
    overflow: hidden;
    margin-bottom: 10px;
    border-radius: 10px;
    background: #ffffff;
}

.card-image img {
    max-width: 100%;
    width: 100%;
    height: 100%;
    max-height: 100%;
    object-fit: cover;
    object-position: center center;
}

.location i.pi.pi-map-marker {
    font-size: 1.8vh;
}

.ai-support-text {
    border: 1px solid #ffbdbd;
    background: #fff0f0;
    padding: 0.4vh 2vh;
    height: 4vh;
    border-radius: 10px;
    font-size: 1.8vh;
    font-weight: 500;
    color: #000000;
    white-space: nowrap;
    order: -1;
    margin-right: 8px;
}

.chat-container .p-scrollpanel-content {
    overflow-x: hidden !important;
}

.chat-container .p-scrollpanel-bar.p-scrollpanel-bar-x {
    display: none !important;
}

.chatbot-main-header-left {
    display: flex;
    align-items: center;
    column-gap: 5px;
}

.chatbot-main-header {
    /* background: #014679; */
    padding: 10px 16px;
    height: 65px;
    border-bottom: 0.1vh solid #ebebeb;
}

.chatbot-header-icon {
    background: #ffffff;
    border-radius: 1vh;
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.chatbot-main-header-left img {
    width: 5vh;
}

.chatbot-main-header-left h2 {
    font-size: 18px;
    font-weight: 600;
    color: #353535;
    display: flex;
    flex-direction: column;
    line-height: normal;
}

span.online-text {
    font-size: 13px;
    color: #5bb93c;
    padding-top: 0.4vh;
}

.direction-rtl .ai-support-text {
    order: 1;
    margin-right: 0;
    margin-left: 8px;
}

.direction-rtl .bot-message-header {
    padding-right: 0 !important;
    padding-left: 2vh !important;
}

.direction-rtl .p-button.minimize-chat {
    right: inherit !important;
    left: 1vh !important;
}

.direction-rtl .chatbot-container {
    /* right: inherit; */
    right: 10px;
    align-items: flex-start;
}

.direction-rtl .chat-footer button.p-button.p-component.p-button-icon-only {
    right: inherit !important;
    left: 2.6vh !important;
    margin: 0;
}

.direction-rtl .chat-footer input {
    padding-right: 15px;
    padding-left: 40px;
    text-align: right !important;
}

.direction-rtl .chat-footer input::placeholder {
    text-align: right !important;
}

.direction-rtl .chat-container .p-scrollpanel-content {
    padding: 0 0px 18px 18px !important;
}

.direction-rtl .message.bot-message .swiper .swiper-slide p.description,
.direction-rtl .message.bot-message .swiper .swiper-slide h2.title,
.direction-rtl p.cousines,
.direction-rtl p.location {
    text-align: right;
}
</style>